import { useEffect, useState } from "react";
import { removeNodeAtPath, addNodeUnderParent } from "react-sortable-tree";
import { isEmpty, omit, uniq } from "lodash";

import { useFetchBranches, useUpdateBranches } from "@lhd/hooks";
import { getChanges } from "@lhd/utility";

export type ReceivedProps = Record<string, any>;

const useBranches = (props: ReceivedProps) => {
  const { data: branchData, isLoading, isSuccess } = useFetchBranches();
  const { mutate, isLoading: isSubmitting } = useUpdateBranches();

  const [currentNode, setCurrentNode] = useState<any>({});
  const [treeData, setTreeData] = useState<any>([]);
  const [originalData, setOriginalData] = useState<any>([]);
  const [deleteIds, setDeleteIds] = useState<number[]>([]);
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const companyId = localStorage.getItem("company_id");

  const updateTreeData = (treeData: any) => setTreeData(treeData);

  const removeNode = (node: any, path: any) => {
    if (node?.id) {
      setDeleteIds(uniq([...deleteIds, node.id]));
    }

    const result = removeNodeAtPath({
      treeData: treeData,
      path,
      getNodeKey: ({ treeIndex }) => treeIndex,
    });
    setTreeData(result);
  };

  const selectThis = (node: any, path: any) =>
    setCurrentNode({ currentNode: node, path: path });

  const insertNewNode = (path: any) => {
    const result = addNodeUnderParent({
      treeData: treeData,
      expandParent: true,
      newNode: {
        title: "",
        company_id: companyId,
        action: "create",
        expanded: true,
        isDelete: true,
        isDrag: true,
        children: [],
      },
      parentKey: path[path.length - 1],
      getNodeKey: ({ treeIndex }) => treeIndex,
    }).treeData;

    setTreeData(result);
  };

  const onAddNode = () => {
    const result = addNodeUnderParent({
      treeData: treeData,
      parentKey: undefined,
      expandParent: true,
      getNodeKey: ({ treeIndex }) => treeIndex,
      newNode: {
        title: null,
        company_id: companyId,
        action: "create",
        expanded: true,
        isDelete: true,
        isDrag: true,
        children: [],
      },
    }).treeData;
    setTreeData(result);
  };

  const onSubmit = async () => {
    const parseData = (dataArray: any) => {
      const res = dataArray.map((i: any) => {
        const parsed = Object.keys(i).reduce((arr, cur) => {
          return {
            ...omit(arr, ["expanded", "isDelete", "isDrag", "branch_id"]),
            [cur === "title" ? "name" : cur]:
              cur !== "children" ? i[cur] : parseData(i[cur]),
          };
        }, {});
        return parsed;
      });
      return res;
    };

    const payload = parseData(treeData);

    mutate(
      {
        payload,
        deleteIds,
      },
      {
        onSuccess() {
          setDeleteIds([]);
          setIsEditing(false);
        },
      },
    );
  };

  useEffect(() => {
    if (!isEmpty(treeData)) {
      const isDifferent = getChanges(originalData, treeData);
      if (!isEmpty(isDifferent)) {
        setIsEditing(true);
      } else {
        setIsEditing(false);
      }
    }
  }, [treeData, isEditing, originalData]);

  useEffect(() => {
    if (isSuccess) {
      const parseData = (dataArray: any) => {
        const res = dataArray.map((i: any) => {
          const parsed = Object.keys(i).reduce((arr, cur) => {
            return {
              ...omit(arr, ["created_at", "updated_at", "ancestry"]),
              expanded: true,
              isDelete: true,
              isDrag: true,
              [cur === "name" ? "title" : cur]:
                cur !== "children" ? i[cur] : parseData(i[cur]),
            };
          }, {});
          return parsed;
        });
        return res;
      };

      setTreeData(parseData(branchData));
      setOriginalData(parseData(branchData));
    }
  }, [branchData, isSuccess]);

  return {
    ...props,
    treeData,
    currentNode,
    isLoading,
    isSubmitting,
    isEditing,
    onAddNode,
    onSubmit,
    setTreeData,
    updateTreeData,
    removeNode,
    selectThis,
    insertNewNode,
  };
};

export type Props = ReturnType<typeof useBranches>;

export default useBranches;
